import { Stack, HStack, Box, Flex, defineStyle, Icon } from '@chakra-ui/react';
import { Avatar, RobotIcon, OfficialIcon, Tooltip } from '@halo/ui-chat-react';
import { type User } from '@halo/chat-sdk-react';

interface HeaderProps {
  user?: User | null;
  bio?: string;
  tooltip?: string;
  official?: boolean;
}

const Header = ({ user, bio, tooltip, official }: HeaderProps) => {
  return (
    <>
      <Stack
        className="StackHeader"
        gap="0"
        bg="bg.subtle"
        w="full"
        pb="2.5"
        roundedTopLeft="2xl"
        mt="-2.5"
      >
        <Box bg="transparent">
          <HStack className="HStack" gap="2.5" bg="bg" pl="4" roundedTopLeft="2xl">
            <Flex alignItems="center" gap="2.5" marginTop="-14px">
              <Avatar
                css={defineStyle({
                  outlineWidth: '3px',
                  outlineColor: 'bg',
                  outlineStyle: 'solid',
                })}
                src={user?.avatar}
                name={user?.name}
                w={86}
                height={86}
                activeShake={false}
              />
            </Flex>
            <Box flex="1 1 auto" overflow="hidden">
              <Flex
                flex="1"
                fontSize="xl"
                gap="1"
                mb="0.5"
                pr="4"
                alignItems="center"
                justifyContent="space-between"
              >
                <Flex
                  alignItems="center"
                  gap="1"
                  overflow="hidden"
                  css={{
                    '&:hover': {
                      '--show-copy-name': 'block',
                    },
                  }}
                  flex="1"
                >
                  <Icon>
                    <RobotIcon
                      style={{
                        width: 20,
                        height: 20,
                      }}
                    />
                  </Icon>
                  <Box
                    truncate
                    lineHeight="shorter"
                    fontWeight="semibold"
                    cursor="pointer"
                    maxW="80%"
                  >
                    {user?.name}
                  </Box>
                  {official && (
                    <Tooltip
                      showArrow={true}
                      content={tooltip}
                      openDelay={0}
                      closeDelay={0}
                      positioning={{
                        placement: 'top',
                      }}
                    >
                      <OfficialIcon />
                    </Tooltip>
                  )}
                </Flex>
              </Flex>
              <Flex
                alignItems="center"
                fontSize="sm"
                color="fg.subtle"
                fontWeight="medium"
                css={{
                  '&:hover': {
                    '--show-copy-name': 'block',
                  },
                }}
              >
                <Box minW="50px" truncate cursor="pointer">
                  @{user?.username}
                </Box>
              </Flex>
            </Box>
          </HStack>
        </Box>
        <Box
          py="2.5"
          px="5"
          bg="linear-gradient(to bottom, var(--colors-bg), var(--colors-bg-subtle))"
        >
          <HStack
            color="fg"
            fontSize="sm"
            fontWeight="medium"
            overflow="hidden"
            gap="1"
            css={{
              '&:hover': {
                '--show-edit-bio': 'block',
              },
            }}
          >
            {bio}
          </HStack>
        </Box>
      </Stack>
    </>
  );
};

const MemoizedHeader = memo(Header);
export { MemoizedHeader as Header };
